import { sessionRealm, type SessionOwner } from '@/stores/sessionRecovery';
import { create } from 'zustand';
import type { ProtocolByRegion } from '@valiosascrm/shared';
import type { ClinicalSession, CreateSessionData } from '@/services/clinical';

export interface ClinicalValues {
  observations: string;
  regionProtocol: ProtocolByRegion;
}
export interface ClinicalDraft {
  value: ClinicalValues;
  baseline: ClinicalValues;
  session: ClinicalSession | null;
  revision: number;
  saving: boolean;
  operationId?: string;
  recovered?: boolean;
  createAttempt?: { data: CreateSessionData; revision: number };
  conflict?: ClinicalSession | null;
}
export interface ConversationDraft {
  text: string;
  quotedMessageId?: string;
  revision: number;
  sending: boolean;
  operationId?: string;
  recovered?: boolean;
  clientMessageId?: string;
  deliveryState?: 'pending' | 'unknown';
}

/**
 * Story 65.45 — bloco de um serviço do atendimento (evolução do serviço, parâmetros do laser,
 * regiões). A chave leva clínica, pessoa, tipo de sessão, atendimento e serviço.
 */
export interface ServiceBlockDraft {
  /** O bloco como a tela o desenha: valores, marcas de campo mexido e de região nova. */
  value: unknown;
  /** Impressão do registro salvo contra o qual a pessoa digitou. */
  baseline: string;
  /** Revisão do registro quando ela começou a digitar; `null` se ainda não havia registro. */
  revision: number | null;
  dirty: boolean;
  recovered?: boolean;
}

type Owner = SessionOwner;
type DraftMaps = {
  clinical: Record<string, ClinicalDraft>;
  conversations: Record<string, ConversationDraft>;
  services: Record<string, ServiceBlockDraft>;
};
// `origin` e `email` só existem no que a queda da sessão pôs de lado: é para lá, e para essa
// pessoa, que o login devolve o trabalho. Tudo em memória.
type Shelved = DraftMaps & { owner: Owner; origin?: string | null; email?: string | null };
let quarantine: Shelved | null = null;
const parked = new Map<string, Shelved>();
// O último login devolveu trabalho posto de lado pela queda da sessão: para onde voltar.
let recoveredOrigin: string | null | undefined;
const EMPTY_MAPS = { clinical: {}, conversations: {}, services: {} };
const holdsWork = (maps: DraftMaps) =>
  Object.keys(maps.clinical).length > 0 || Object.keys(maps.conversations).length > 0 || Object.keys(maps.services).length > 0;
const ownerKey = (owner: Owner) => JSON.stringify([owner.id, owner.tenantId, sessionRealm(owner)]);
const sameOwner = (a: Owner, b: Owner) => a.id === b.id && a.tenantId === b.tenantId && sessionRealm(a) === sessionRealm(b);

export function draftKey(user: Owner | null, domain: string, id?: string) {
  return user?.id && user.tenantId && id ? JSON.stringify([user.tenantId, user.id, sessionRealm(user), domain, id]) : null;
}
export function isClinicalDirty(draft: ClinicalDraft) {
  return JSON.stringify(draft.value) !== JSON.stringify(draft.baseline);
}

// Health records and messages deliberately stay in memory, never browser storage.
// An epoch prevents an old request from repopulating drafts after logout/tenant switch.
export const useWorkDrafts = create<{
  epoch: number;
  clinical: Record<string, ClinicalDraft>;
  conversations: Record<string, ConversationDraft>;
  services: Record<string, ServiceBlockDraft>;
  clear: () => void;
}>((set) => ({
  epoch: 0,
  ...EMPTY_MAPS,
  clear: () => {
    quarantine = null;
    recoveredOrigin = undefined;
    parked.clear();
    set((state) => ({ epoch: state.epoch + 1, ...EMPTY_MAPS }));
  },
}));

/** Só caminho da própria aplicação, e nunca uma tela de entrada. */
export function isInternalRoute(route: string | null | undefined): route is string {
  return typeof route === 'string' && /^\/(?![/\\])/.test(route) &&
    !/^\/(login|register|forgot-password|reset-password|accept-invite|onboarding-empresa|admin)(?:[/?#]|$)/.test(route);
}

export function quarantineExpiredWork(owner: (Owner & { email?: string | null }) | null) {
  recoveredOrigin = undefined;
  if (owner) {
    const current = useWorkDrafts.getState();
    const clinical = Object.fromEntries(Object.entries(current.clinical)
      .filter(([, draft]) => draft.saving || isClinicalDirty(draft))
      .map(([key, draft]) => [key, { ...draft, saving: false, recovered: true }]));
    const conversations = Object.fromEntries(Object.entries(current.conversations)
      .filter(([, draft]) => draft.text || draft.quotedMessageId)
      .map(([key, draft]) => [key, { ...draft, sending: false, recovered: true }]));
    const services = Object.fromEntries(Object.entries(current.services)
      .filter(([, draft]) => draft.dirty)
      .map(([key, draft]) => [key, { ...draft, recovered: true }]));
    if (holdsWork({ clinical, conversations, services })) {
      quarantine = {
        owner: { id: owner.id, tenantId: owner.tenantId, impersonation: owner.impersonation ? { sessionId: owner.impersonation.sessionId } : undefined },
        clinical, conversations, services,
        // A tela onde a pessoa estava: fica só aqui, nunca no endereço nem no navegador.
        origin: typeof window === 'undefined' ? null : `${window.location.pathname}${window.location.search}`,
        email: owner.email?.trim().toLowerCase() || null,
      };
    }
  }
  useWorkDrafts.setState((state) => ({ epoch: state.epoch + 1, ...EMPTY_MAPS }));
}

export function reconcileDraftOwner(owner: Owner) {
  for (const [key, entry] of parked) if (entry.owner.id !== owner.id || sessionRealm(entry.owner) !== sessionRealm(owner)) parked.delete(key);
  const expired = quarantine && sameOwner(quarantine.owner, owner) ? quarantine : null;
  const restore = expired ?? parked.get(ownerKey(owner));
  parked.delete(ownerKey(owner));
  quarantine = null;
  recoveredOrigin = expired ? expired.origin ?? null : undefined;
  useWorkDrafts.setState((state) => ({
    epoch: state.epoch + 1,
    clinical: restore?.clinical ?? {},
    conversations: restore?.conversations ?? {},
    services: restore?.services ?? {},
  }));
}

/**
 * Story 65.45 — a clínica em que a sessão caiu, para o login voltar a ela. Só para a mesma
 * pessoa (o e-mail digitado), em sessão normal, e só enquanto houver trabalho guardado.
 */
export function shelvedClinicFor(email: string | null | undefined): string | null {
  const wanted = email?.trim().toLowerCase();
  if (!quarantine || !wanted || quarantine.email !== wanted || quarantine.owner.impersonation) return null;
  return quarantine.owner.tenantId;
}

/**
 * Chamado pelo login depois de confirmar a sessão. `null`: nada foi devolvido — o login recarrega
 * a página como sempre (RC-063). Rota: o trabalho voltou e é para lá que a pessoa vai, sem
 * recarregar. Trabalho devolvido sem rota interna vale o destino padrão e é descartado.
 */
export function takeRecoveredOrigin(): string | null {
  const origin = recoveredOrigin;
  recoveredOrigin = undefined;
  if (origin === undefined) return null;
  if (isInternalRoute(origin) && hasUnsavedWork()) return origin;
  useWorkDrafts.setState((state) => ({ epoch: state.epoch + 1, ...EMPTY_MAPS }));
  return null;
}

/** Explicitly following another clinic parks recoverable work without exposing it there. */
export function parkClinicWork(owner: Owner) {
  quarantineExpiredWork(owner);
  if (quarantine && sameOwner(quarantine.owner, owner)) parked.set(ownerKey(owner), quarantine);
  quarantine = null;
}

// Late replies may reconcile an existing matching operation, even after reauthentication.
// They can never recreate a discarded entry or write to a different user's draft.
export function settleClinicalOperation(key: string, operationId: string, patch: (draft: ClinicalDraft) => ClinicalDraft) {
  useWorkDrafts.setState((state) => {
    const draft = state.clinical[key];
    return draft?.operationId === operationId ? { clinical: { ...state.clinical, [key]: patch(draft) } } : state;
  });
  const hidden = quarantine?.clinical[key];
  if (hidden?.operationId === operationId && quarantine) quarantine.clinical[key] = patch(hidden);
  for (const entry of parked.values()) {
    const draft = entry.clinical[key];
    if (draft?.operationId === operationId) entry.clinical[key] = patch(draft);
  }
}

export function settleConversationOperation(key: string, operationId: string, patch: (draft: ConversationDraft) => ConversationDraft | null) {
  const apply = (drafts: Record<string, ConversationDraft>) => {
    const draft = drafts[key];
    if (draft?.operationId !== operationId) return drafts;
    const next = { ...drafts };
    const changed = patch(draft);
    if (changed) next[key] = changed;
    else delete next[key];
    return next;
  };
  useWorkDrafts.setState((state) => ({ conversations: apply(state.conversations) }));
  if (quarantine) quarantine.conversations = apply(quarantine.conversations);
  for (const entry of parked.values()) entry.conversations = apply(entry.conversations);
}

export function hasUnsavedWork() {
  const state = useWorkDrafts.getState();
  return Boolean(quarantine && holdsWork(quarantine)) ||
    Object.values(state.clinical).some((draft) => draft.saving || isClinicalDirty(draft)) ||
    Object.values(state.conversations).some((draft) => Boolean(draft.text || draft.quotedMessageId || draft.sending)) ||
    Object.values(state.services).some((draft) => draft.dirty);
}

/** Closing this tab also loses parked work from other clinics, unlike an in-app switch. */
export function hasWorkBeforeUnload() {
  return hasUnsavedWork() || Array.from(parked.values()).some(holdsWork);
}
